import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import StaticPageShell, { StaticCtaCard, StaticSection } from '../components/StaticPageShell';

const CommunityRules = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);

    const rules = [
        {
            icon: '📸',
            titleKey: 'page.rules.accurate.title',
            titleFb: '1. Accurate listings only',
            textKey: 'page.rules.accurate.text',
            textFb:
                'All baby items must be described truthfully (condition, age, defects). Photos must show the actual item you are selling. Textile items must be washed before listing.',
        },
        {
            icon: '🛡️',
            titleKey: 'page.rules.safety.title',
            titleFb: '2. Safety first',
            textKey: 'page.rules.safety.text',
            textFb:
                'No recalled items, no broken safety equipment, no cribs or car seats older than 6 years unless explicitly approved. We follow EU and Belgian product safety standards.',
        },
        {
            icon: '⛔',
            titleKey: 'page.rules.prohibited.title',
            titleFb: '3. No prohibited items',
            textKey: 'page.rules.prohibited.text',
            textFb:
                'No food, medication, used pacifiers or bottle teats, weapons, or counterfeit branded goods.',
        },
        {
            // Title kept for i18n key stability; chat feature deferred
            icon: '🤝',
            titleKey: 'page.rules.respect.title',
            titleFb: '4. Respectful behaviour',
            // was: '4. Respectful chat & behaviour'
            textKey: 'page.rules.respect.text',
            textFb:
                'Treat other parents with respect. Harassment, discrimination, or spam leads to warnings; three strikes may result in account suspension.',
        },
        {
            icon: '🔒',
            titleKey: 'page.rules.privacy.title',
            titleFb: '5. Privacy',
            textKey: 'page.rules.privacy.text',
            textFb:
                "Do not share other users' personal data outside the platform. Meet in safe public places for local pickups across Belgium.",
        },
        {
            icon: '⚖️',
            titleKey: 'page.rules.enforcement.title',
            titleFb: '6. Enforcement',
            textKey: 'page.rules.enforcement.text',
            textFb:
                'Petito moderators may remove listings, issue strikes, or suspend accounts that breach these rules. Report suspicious listings via the report button or helpdesk.',
        },
    ];

    return (
        <StaticPageShell
            eyebrow={tr('page.rules.eyebrow', 'Community')}
            title={tr('page.rules.title', 'Community Rules – Petito')}
            subtitle={tr('page.rules.updated', 'Last updated: June 2026')}
            maxWidth="3xl"
        >
            <p className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] px-5 py-4 text-sm leading-relaxed text-[var(--text-color)]/90 sm:text-base">
                {tr(
                    'page.rules.intro',
                    'These rules keep Petito safe and fair for every parent. By listing or buying, you agree to follow them.'
                )}
            </p>

            {rules.map((r) => (
                <StaticSection key={r.titleKey} icon={r.icon} title={tr(r.titleKey, r.titleFb)}>
                    <p>{tr(r.textKey, r.textFb)}</p>
                </StaticSection>
            ))}

            <StaticCtaCard>
                <p className="mb-3">
                    {tr(
                        'page.rules.report.cta',
                        'See something that breaks the rules?'
                    )}
                </p>
                <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link
                        to="/helpdesk"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 sm:w-auto"
                    >
                        {tr('page.faq.contact.link', 'Contact our helpdesk')}
                    </Link>
                    <Link
                        to="/terms"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-[var(--accent-color)] bg-white/60 px-6 py-2.5 text-sm font-semibold text-[var(--text-color)] transition hover:bg-white/90 sm:w-auto"
                    >
                        {tr('page.terms.title', 'Terms of Service')}
                    </Link>
                </div>
            </StaticCtaCard>
        </StaticPageShell>
    );
};

export default CommunityRules;
